import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  MONTHLY_OPERATING_EXPENSES,
  MONTHLY_REVENUE,
  TODAY,
} from "@/data/financial-systems";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";
import { getCurrencySymbol } from "@/utils/currency";

// BuildRight seasonal pattern — construction industry
// Q1 (Apr-Jun): high activity, good inflows
// Q2 (Jul-Sep): peak summer — very high
// Q3 (Oct-Dec): slowdown, planning phase
// Q4 (Jan-Mar): recovery, tender wins
const SEASONAL_INDEX: Record<string, number> = {
  Jan: 0.72,
  Feb: 0.81,
  Mar: 1.05,
  Apr: 1.18,
  May: 1.25,
  Jun: 1.3,
  Jul: 1.28,
  Aug: 1.15,
  Sep: 1.1,
  Oct: 0.95,
  Nov: 0.85,
  Dec: 0.65,
};

export const SeasonalTrendsPreview: React.FC = () => {
  const { getCashMovementTerms } = useTerminology();
  const { formatCurrency } = useFormatting();
  const terms = getCashMovementTerms();

  const currentMonthIdx = TODAY.getMonth(); // 0-based

  // Build 24 months of historical data (2 years back)
  const months: {
    label: string;
    monthShort: string;
    year: number;
    inflows: number;
    outflows: number;
    net: number;
    index: number;
    isCurrent: boolean;
  }[] = [];

  for (let i = 23; i >= 0; i--) {
    const d = new Date(TODAY.getFullYear(), TODAY.getMonth() - i, 1);
    const monthShort = d.toLocaleDateString("en-GB", { month: "short" });
    const year = d.getFullYear();
    const idx = SEASONAL_INDEX[monthShort] ?? 1;
    const inflows = Math.round(MONTHLY_REVENUE * idx);
    const outflows = Math.round(MONTHLY_OPERATING_EXPENSES * (0.9 + idx * 0.1)); // costs less variable
    months.push({
      label: `${monthShort} ${String(year).slice(2)}`,
      monthShort,
      year,
      inflows,
      outflows,
      net: inflows - outflows,
      index: idx,
      isCurrent: i === 0,
    });
  }

  // Quarterly averages
  const quarters = [
    { label: "Q1 Apr–Jun", months: ["Apr", "May", "Jun"] },
    { label: "Q2 Jul–Sep", months: ["Jul", "Aug", "Sep"] },
    { label: "Q3 Oct–Dec", months: ["Oct", "Nov", "Dec"] },
    { label: "Q4 Jan–Mar", months: ["Jan", "Feb", "Mar"] },
  ].map((q) => {
    const avg = q.months.reduce((s, m) => s + (SEASONAL_INDEX[m] ?? 1), 0) / 3;
    const avgInflows = Math.round(MONTHLY_REVENUE * avg);
    const avgOutflows = Math.round(
      MONTHLY_OPERATING_EXPENSES * (0.9 + avg * 0.1),
    );
    return {
      ...q,
      avgInflows,
      avgOutflows,
      avgNet: avgInflows - avgOutflows,
      index: avg,
    };
  });

  // Best/worst months
  const best = [...months].sort((a, b) => b.net - a.net)[0];
  const worst = [...months].sort((a, b) => a.net - b.net)[0];

  // Chart data — last 12 months only for readability
  const chartData = months.slice(12);

  return (
    <div className="space-y-6">
      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800 p-4">
          <p className="text-xs text-muted-foreground mb-1">Peak Month (avg)</p>
          <p className="text-xl font-bold text-green-700 dark:text-green-400">
            June
          </p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(Math.round(MONTHLY_REVENUE * 1.3))} avg{" "}
            {terms.in.toLowerCase()}
          </p>
        </div>
        <div className="rounded-lg border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Lowest Month (avg)
          </p>
          <p className="text-xl font-bold text-red-700 dark:text-red-400">
            December
          </p>
          <p className="text-sm text-muted-foreground">
            {formatCurrency(Math.round(MONTHLY_REVENUE * 0.65))} avg{" "}
            {terms.in.toLowerCase()}
          </p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4">
          <p className="text-xs text-muted-foreground mb-1">
            Peak-to-Trough Swing
          </p>
          <p className="text-xl font-bold">
            {formatCurrency(Math.round(MONTHLY_REVENUE * (1.3 - 0.65)))}
          </p>
          <p className="text-sm text-muted-foreground">
            Monthly {terms.in.toLowerCase()} difference
          </p>
        </div>
      </div>

      {/* 12-month bar chart */}
      <div>
        <p className="text-sm font-semibold mb-3">
          Monthly {terms.in} vs Costs — Last 12 Months
        </p>
        <div className="h-56 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
              />
              <YAxis
                tickFormatter={(v) =>
                  `${getCurrencySymbol()}${(v / 1000).toFixed(0)}k`
                }
                tick={{ fontSize: 10 }}
                className="fill-muted-foreground"
              />
              <Tooltip
                formatter={
                  ((v: number, name: string) => [
                    formatCurrency(v),
                    name === "inflows" ? terms.in : terms.out,
                  ]) as never
                }
                contentStyle={{ fontSize: 12 }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                formatter={(value) =>
                  value === "inflows" ? terms.in : terms.out
                }
              />
              <Bar
                dataKey="inflows"
                fill="#22c55e"
                radius={[2, 2, 0, 0]}
                name="inflows"
              />
              <Bar
                dataKey="outflows"
                fill="#ef4444"
                radius={[2, 2, 0, 0]}
                name="outflows"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Quarterly seasonal summary */}
      <div>
        <p className="text-sm font-semibold mb-3">Quarterly Seasonal Pattern</p>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/60">
                <th className="px-4 py-3 text-left font-semibold">Quarter</th>
                <th className="px-4 py-3 text-right font-semibold">
                  Seasonal Index
                </th>
                <th className="px-4 py-3 text-right font-semibold text-green-700 dark:text-green-400">
                  Avg {terms.in}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-red-700 dark:text-red-400">
                  Avg Costs
                </th>
                <th className="px-4 py-3 text-right font-semibold">Avg Net</th>
                <th className="px-4 py-3 text-center font-semibold">Pattern</th>
              </tr>
            </thead>
            <tbody>
              {quarters.map((q) => {
                const trend =
                  q.index >= 1.1 ? "high" : q.index >= 0.9 ? "mid" : "low";
                return (
                  <tr
                    key={q.label}
                    className="border-b hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-2.5 font-medium">{q.label}</td>
                    <td className="px-4 py-2.5 text-right text-muted-foreground">
                      {q.index.toFixed(2)}×
                    </td>
                    <td className="px-4 py-2.5 text-right text-green-700 dark:text-green-400">
                      {formatCurrency(q.avgInflows)}
                    </td>
                    <td className="px-4 py-2.5 text-right text-red-700 dark:text-red-400">
                      {formatCurrency(q.avgOutflows)}
                    </td>
                    <td
                      className={`px-4 py-2.5 text-right font-medium ${q.avgNet >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
                    >
                      {q.avgNet >= 0 ? "+" : ""}
                      {formatCurrency(q.avgNet)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {trend === "high" ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <TrendingUp className="h-3 w-3" /> Peak
                        </span>
                      ) : trend === "low" ? (
                        <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium">
                          <TrendingDown className="h-3 w-3" /> Low
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-muted-foreground text-xs">
                          <Minus className="h-3 w-3" /> Moderate
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Seasonal indices calculated from 24 months of historical data (Access
        Financials) · Construction industry seasonal patterns applied · Indices
        above 1.0 indicate above-average activity
      </p>
    </div>
  );
};
